/**
 * cropImageWeb — web implementation of `PlatformAdapter.cropImage`, backing the
 * `cropImage` ActionFlow action (Lane B-6). The action is fire-and-forget (no
 * result is read by the flow), so the web behavior is a VISUAL crop applied
 * directly to the target `<img>`: the element identified by `imageId` is
 * constrained to the requested aspect `ratio` ("W:H") with a centered cover fit.
 *
 * No-op (with a warning) when the image element isn't found or the ratio is
 * malformed. Safe to call in a non-DOM environment (guards on `document`).
 */

/** Parse a "W:H" (or "W/H") ratio into a CSS `aspect-ratio` value, or null if malformed. */
export function parseRatio(ratio: string): string | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/.exec(ratio)
  if (!m) return null
  const w = Number(m[1])
  const h = Number(m[2])
  if (!(w > 0) || !(h > 0)) return null
  return `${w} / ${h}`
}

export function cropImageWeb(imageId: string, ratio?: string): void {
  if (typeof document === "undefined") return
  const el = document.getElementById(imageId)
  if (!(el instanceof HTMLImageElement)) {
    // eslint-disable-next-line no-console
    console.warn(`[cropImageWeb] no <img> with id "${imageId}"`)
    return
  }
  if (!ratio) {
    el.style.objectFit = "cover"
    return
  }
  const aspect = parseRatio(ratio)
  if (!aspect) {
    // eslint-disable-next-line no-console
    console.warn(`[cropImageWeb] malformed ratio "${ratio}" (expected "W:H")`)
    return
  }
  el.style.aspectRatio = aspect
  el.style.objectFit = "cover"
}
